import { useLicenseDetails } from "../../contexts/licenseDetails";
import Button from "../Button/Button";
import { useEffect, useState } from "react";
import Input from "../Input/Input";
import { BillingPeriodText } from "../../constants/billing";
import { LicenseFeaturesSelector } from "../LicenseFeatureSelector/LicenseFeatureSelector";
import ToggleButton from "../ToggleButton/ToggleButton";
import LicenseService from "../../services/LicensesService";
import {
   getZodFieldErrors,
   licenseFeaturesSchema,
} from "../../validations/license-subpages.schema";

export function LicenseUpdateFeaturesPage({ license, ...props }) {
   const { setCurrentSubTab } = useLicenseDetails();

   const [featureIds, setFeatureIds] = useState([]);
   const [typeFeatures, setTypeFeatures] = useState([]);
   const [metadata, setMetadata] = useState({});
   const [notifyOwner, setNotifyOwner] = useState(true);
   const [errors, setErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   useEffect(() => {
      if (!license) {
         setFeatureIds([]);
         setTypeFeatures([]);
         setMetadata({});
         return;
      }
      setMetadata(license.metadata || {});
      setFeatureIds(license.features?.map((feature) => String(feature.id)) || []);
   }, [license]);

   const availableFeatures = typeFeatures.length ? typeFeatures : license?.features ?? [];
   const hasBackupFeature = availableFeatures.some(
      feature => feature.code === "F-B" && featureIds.includes(String(feature.id))
   );

   async function handleSubmit(event) {
      event.preventDefault();
      const validation = licenseFeaturesSchema.safeParse({
         featureIds,
         hasBackupFeature,
         enabledBackup: metadata.enabledBackup,
         enabledRotation: metadata.enabledRotation,
         backupSize:
            metadata.backupSize == null || metadata.backupSize === ""
               ? undefined
               : Number(metadata.backupSize),
      });
      if (!validation.success) {
         setErrors(getZodFieldErrors(validation.error));
         return;
      }
      setErrors({});
      setIsSubmitting(true);
      try {
         const response = await LicenseService.changeLicenseFeatures(license.id, {
            featureIds,
            notifyOwner,
            notifyInAdvance: false,
            reason: "Atualização de recursos da licença",
            enabledBackup: metadata.enabledBackup,
            enabledRotation: metadata.enabledRotation,
            backupSize: metadata.backupSize,
         });
         if (response?.status >= 200 && response.status < 300) {
            setCurrentSubTab(null);
         }
      } finally {
         setIsSubmitting(false);
      }
   }

   return (
      <div className="fade-in mt-0.5!">
         <div className="flex flex-row items-center justify-between">
            <h2 className="font-bold! text-[15px]!">Atualizar recursos da licença</h2>
         </div>
         <p className="mt-3! opacity-60 leading-snug!">
            Se a licença possuir renovação automática, as alterações serão aplicadas na próxima vigência do ciclo {BillingPeriodText[license.currentBilling?.period]?.toLowerCase()} de cobrança.
         </p>
         <form
            className="flex flex-col gap-3 mt-7!"
            onSubmit={handleSubmit}
         >
            <div className="w-full">
               <LicenseFeaturesSelector
                  typeCode={license.type.code}
                  values={featureIds}
                  onChange={setFeatureIds}
                  valueProperty="id"
                  onFeaturesLoaded={setTypeFeatures}
                  isSubmitting={isSubmitting}
                  error={errors.featureIds}
               />
            </div>

            {hasBackupFeature && (
               <div className="flex flex-col mt-4! gap-3">
                  <ToggleButton
                     label={`${metadata.enabledBackup ? "Backup Habilitado" : "Backup Desabilitado"
                        }`}
                     selected={metadata.enabledBackup}
                        onClick={() => {
                           const enabledBackup = !metadata.enabledBackup;
                           setMetadata({
                              ...metadata,
                              enabledBackup,
                              enabledRotation: enabledBackup ? metadata.enabledRotation : false,
                           });
                        }}
                     disabled={props.type === "consulta"}
                     tooltipVisible={true}
                     tooltipText={
                        "Habilita ou desabilita o backup, quando habilitado o sistema irá realizar backup dos dados do equipamento para o cliente."
                     }
                  />
                  <ToggleButton
                     label={`${metadata.enabledRotation ? "Rotação de Backup Habilitada" : "Rotação de Backup Desabilitada"
                        }`}
                     selected={metadata.enabledRotation}
                     onClick={() => setMetadata({ ...metadata, enabledRotation: !metadata.enabledRotation })}
                     disabled={props.type === "consulta" || !metadata.enabledBackup}
                     tooltipVisible={true}
                     tooltipText={
                        "Habilita ou desabilita a rotação de backup, quando habilitado, quando o espaço para um novo backup não for suficiente, o mais antigo é removido."
                     }
                  />
                  {errors.enabledRotation && (
                     <span className="error-message">{errors.enabledRotation}</span>
                  )}
                  <div className="w-full mt-2!">
                     <Input
                        name={"backupSize"}
                        id="backupSize"
                        type="number"
                        labelText={
                           metadata.enabledBackup
                              ? "Tamanho máximo para backup (Bytes) *"
                              : "Tamanho máximo para backup (Bytes)"
                        }
                        value={metadata.backupSize}
                        onChange={({ target }) => setMetadata({ ...metadata, backupSize: Number(target.value) })}
                        disabled={props.type === "consulta" || !metadata.enabledBackup}
                        error={errors.backupSize}
                        tooltipVisible={true}
                        tooltipText={
                           "Tamanho máximo para backup, o sistema irá realizar backup dos dados do equipamento para o cliente."
                        }
                     />
                  </div>
               </div>
            )}

            <ToggleButton
               className="mt-4!"
               label="Notificar proprietário"
               selected={notifyOwner}
               onClick={() => setNotifyOwner(prev => !prev)}
               disabled={isSubmitting}
               tooltipVisible={false}
            />

            <div className="w-full flex flex-row items-center justify-end gap-3! mt-6!">
               <Button
                  className={"button-white"}
                  label={"Cancelar"}
                  onClick={() => setCurrentSubTab(null)}
                  disabled={isSubmitting}
               />
               <Button
                  className={"button-blue"}
                  disabled={isSubmitting}
                  label={"Salvar"}
                  type={"submit"}
                  isLoading={isSubmitting}
               />
            </div>
         </form>
      </div>
   );
}